/**
 * Reading the brand's appearance out of a free-form record.
 *
 * `WidgetAppearance.theme` is `Record<string, string | number | boolean> | null`
 * on the wire, deliberately: brand tokens are tenant data, and pinning them to a
 * closed schema would mean an API release every time a brand wants a corner
 * radius. The contract's own words are that it is "rendered, never executed".
 *
 * The cost of that freedom is that this file is a BOUNDARY. Everything below
 * treats the record as untrusted input — the values reach us over the network,
 * are authored in an admin UI, and one of them ends up as a CSS property on a
 * button that lives on someone else's page.
 */
import type { WidgetAppearance } from '../api/contract';

/**
 * `#rgb`, `#rrggbb`, `#rrggbbaa`.
 *
 * Hex only, and not because other CSS colours are ugly. A colour string is
 * assigned into a style property, and the CSS value grammar has corners —
 * `url(...)`, `var(...)`, `image-set(...)` — that turn "a colour" into "a
 * request" or into a reference to a variable this app never defined. Six hex
 * digits cannot do any of that. The loader re-validates against the same shape
 * before it touches the launcher, because by then the value has crossed a
 * postMessage boundary and is untrusted again.
 */
const HEX_COLOUR = /^#[0-9a-f]{3,8}$/iu;

/**
 * The keys a brand might have used for its accent, in order of preference.
 *
 * A list rather than one name because the admin UI that writes these rows is not
 * this app's to see. Reading three plausible spellings is a smaller wrong than
 * silently rendering the default indigo for a brand that set its colour.
 */
const ACCENT_KEYS = ['accent', 'accentColor', 'primary', 'primaryColor'] as const;

/** The brand's launcher/bubble colour, or null when there is nothing usable. */
export function accentColor(appearance: WidgetAppearance | null): string | null {
  const theme = appearance?.theme ?? null;
  if (theme === null) {
    return null;
  }

  for (const key of ACCENT_KEYS) {
    const value = theme[key];
    if (typeof value === 'string' && HEX_COLOUR.test(value.trim())) {
      return value.trim();
    }
  }
  return null;
}

/**
 * The brand's logo, or null.
 *
 * `logoUrl` is `z.url()` server-side, which admits `javascript:` and `data:` —
 * both legal URLs. This is an `<img src>`, so `javascript:` is inert there, but
 * the scheme check stays because "inert in the element we happen to use today" is
 * not a property worth depending on.
 */
export function logoUrl(appearance: WidgetAppearance | null): string | null {
  const url = appearance?.logoUrl ?? null;
  if (url === null) {
    return null;
  }
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? url : null;
  } catch (notAUrl) {
    if (notAUrl instanceof TypeError) {
      return null;
    }
    throw notAUrl;
  }
}
